"use client";
import { useEffect, useMemo, useState } from "react";
import { getBalance, getCreditLimit, getSettings } from "@/lib/credits";

type Notice = { id: string; level: "info" | "warning" | "error"; title: string; detail?: string; at: number };

export default function NotificationsPanel() {
  const [balance, setBalance] = useState<number>(0);
  const [limit, setLimit] = useState<number>(0);
  const [refillBelow, setRefillBelow] = useState<number>(100);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [b, l, s] = await Promise.all([
          getBalance(),
          getCreditLimit(),
          getSettings(),
        ]);
        if (!cancelled) {
          setBalance(b);
          setLimit(l);
          setRefillBelow(Math.max(0, Number((s as any)?.refillBelow ?? 100)));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const onCredits = () => load();
    try { window.addEventListener("credits-update" as any, onCredits); } catch {}
    return () => { cancelled = true; try { window.removeEventListener("credits-update" as any, onCredits); } catch {} };
  }, []);

  const notices: Notice[] = useMemo(() => {
    const out: Notice[] = [];
    const available = balance + Math.max(0, limit);
    const threshold = Math.max(1, refillBelow);
    if (available <= 0) {
      out.push({ id: 'out', level: 'error', title: 'เครดิตหมด', detail: 'เครดิตของคุณเป็น 0 กรุณาเติมเครดิตเพื่อใช้งานต่อ', at: Date.now() });
    } else if (available <= threshold) {
      out.push({ id: 'low', level: 'warning', title: 'เครดิตใกล้หมด', detail: `คงเหลือ ${available} (เกณฑ์แจ้งเตือน ${threshold}) กรุณาเติมเครดิต`, at: Date.now() });
    }
    // Pull last charge event if present
    try {
      const conv = localStorage.getItem('active_conversation_id') || localStorage.getItem('active_session_id');
      const last = conv ? JSON.parse(localStorage.getItem(`ai_usage_last:${conv}`) || 'null') : null;
      if (last && typeof last === 'object') {
        out.push({ id: 'last-usage', level: 'info', title: 'การใช้งานล่าสุด', detail: `Prompt ${last.prompt} / Completion ${last.completion} (Total ${last.total})`, at: Number(last.at || Date.now()) });
      }
    } catch {}
    return out.sort((a,b) => b.at - a.at);
  }, [balance, limit, refillBelow]);

  return (
    <div className="h-full w-full p-4 text-gray-100">
      <div className="pt-6 pb-3 text-lg font-semibold">Notifications</div>
      {/* No inline banner; keep top toast behavior */}
      <div className="text-sm text-gray-400 mb-3">Credit status and recent usage</div>
      <div className="space-y-2">
        <div className="rounded-md border border-slate-700 bg-slate-800/50 p-3 text-sm">
          <div className="flex justify-between"><span>Balance</span><span className="font-mono">{balance}</span></div>
          <div className="flex justify-between"><span>Limit</span><span className="font-mono">{limit}</span></div>
          <div className="flex justify-between"><span>Low threshold</span><span className="font-mono">{refillBelow}</span></div>
        </div>
        {loading ? (
          <div className="text-gray-400">Loading…</div>
        ) : (
          <ul className="space-y-2">
            {notices.map(n => (
              <li key={n.id} className={`rounded-md p-3 border ${n.level==='error' ? 'bg-red-900/30 border-red-700' : n.level==='warning' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-slate-800/50 border-slate-700'}`}>
                <div className="font-medium">{n.title}</div>
                {n.detail ? <div className="text-gray-300 text-sm mt-0.5">{n.detail}</div> : null}
                {n.level !== 'info' && (
                  <div className="mt-2">
                    <a href="/admin/credits" className="inline-flex items-center rounded bg-indigo-600 text-white text-xs px-2 py-1 hover:bg-indigo-500">เติมเครดิต</a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
